import React, { useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { jobPositions } from "@/data/catalog";
import { submitJobApplication } from "@/lib/api";
import { useAntiBot } from "@/lib/useAntiBot";
import { IMAGES } from "@/lib/site";
import { Check, AlertCircle, Sparkles, Heart, Cpu, Users } from "lucide-react";

const ICONS = [Sparkles, Heart, Cpu, Users];

const TrabajaConNosotros = () => {
  const { t, lang } = useLang();
  const antibot = useAntiBot();
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    position: "",
    experience_years: "",
    message: "",
    consent: false,
  });

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      setStatus({ state: "error", msg: t.contact.f_required });
      return;
    }
    setStatus({ state: "loading", msg: "" });
    try {
      await submitJobApplication({
        name: form.name,
        phone: form.phone,
        email: form.email,
        position: form.position,
        experience_years: form.experience_years ? parseInt(form.experience_years, 10) : null,
        message: form.message,
        consent: form.consent,
        locale: lang,
        hp: antibot.hp,
        t: antibot.startedAt,
      });
      setStatus({ state: "success", msg: t.jobs_page.f_success });
      setForm({ name: "", phone: "", email: "", position: "", experience_years: "", message: "", consent: false });
    } catch (err) {
      setStatus({ state: "error", msg: t.contact.f_error });
    }
  };

  return (
    <main data-testid="jobs-page" className="bg-cream text-ink">
      {/* HERO */}
      <section className="pt-40 pb-16 md:pt-48 md:pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-7">
            <p className="overline">{t.jobs_page.eyebrow}</p>
            <h1 className="mt-6 font-serif text-6xl md:text-8xl font-light tracking-tight leading-[0.95] whitespace-pre-line">
              {t.jobs_page.title}
            </h1>
            <p className="mt-8 max-w-xl text-ink/75 font-light text-lg leading-relaxed">{t.jobs_page.lede}</p>
          </div>
          <div className="md:col-span-5 hidden md:block">
            <div className="aspect-[4/5] bg-ink overflow-hidden">
              <img src={IMAGES.doctorPortrait} alt="Dr. Iñaki Mayo" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-ink text-cream py-24 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="overline text-cream/60">{t.jobs_page.values_title}</p>
          <div className="mt-10 grid md:grid-cols-4 gap-10 md:gap-6">
            {t.jobs_page.values.map((v, i) => {
              const Icon = ICONS[i] || Sparkles;
              return (
                <div key={i} data-testid={`job-value-${i}`} className="border-t border-cream/20 pt-6">
                  <Icon size={22} strokeWidth={1.3} />
                  <p className="mt-5 font-serif text-2xl font-light">{v.t}</p>
                  <p className="mt-3 text-cream/70 font-light leading-relaxed text-sm">{v.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FORM */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <p className="overline">{t.jobs_page.eyebrow}</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl font-light tracking-tight leading-[1.05] max-w-[18ch]">
              {t.jobs_page.title.replace("\n", " ")}
            </h2>
          </div>

          <div className="md:col-span-8">
            <form onSubmit={onSubmit} data-testid="rrhh-form" className="space-y-7">
              {/* Honeypot anti-bot (invisible for humans) */}
              <input {...antibot.honeypotProps} />

              <Field name="name" label={t.contact.f_name} value={form.name} onChange={(v) => onChange("name", v)} />
              <div className="grid md:grid-cols-2 gap-8">
                <Field name="phone" label={t.contact.f_phone} type="tel" value={form.phone} onChange={(v) => onChange("phone", v)} />
                <Field name="email" label={t.contact.f_email} type="email" value={form.email} onChange={(v) => onChange("email", v)} />
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                <div className="md:col-span-2">
                  <label className="block group">
                    <span className="overline block mb-3 text-ink/60">{t.jobs_page.f_position}</span>
                    <select
                      required
                      value={form.position}
                      onChange={(e) => onChange("position", e.target.value)}
                      data-testid="form-field-position"
                      className="w-full bg-transparent border-b border-ink/30 focus:border-ink outline-none py-2 font-light text-lg appearance-none"
                    >
                      <option value="" disabled>—</option>
                      {jobPositions[lang].map((p) => (
                        <option key={p.value} value={p.value} className="bg-cream text-ink">{p.label}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <Field
                  name="experience"
                  label={t.jobs_page.f_experience}
                  type="number"
                  value={form.experience_years}
                  onChange={(v) => onChange("experience_years", v)}
                  required={false}
                  testidOverride="form-field-experience"
                />
              </div>

              <Field name="message" label={t.jobs_page.f_message} multiline value={form.message} onChange={(v) => onChange("message", v)} />

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.consent}
                  onChange={(e) => onChange("consent", e.target.checked)}
                  data-testid="rrhh-consent"
                  className="mt-1 w-4 h-4 border-ink"
                  required
                />
                <span className="text-sm font-light text-ink/75">{t.contact.f_consent}</span>
              </label>

              <button
                type="submit"
                data-testid="rrhh-submit"
                disabled={status.state === "loading"}
                className="px-8 py-4 bg-ink text-cream text-xs tracking-[0.22em] uppercase btn-sweep sweep-cream border border-ink hover:text-ink disabled:opacity-60"
              >
                {status.state === "loading" ? "..." : `${t.jobs_page.f_submit} →`}
              </button>

              {status.state === "success" && (
                <div data-testid="rrhh-success" className="flex items-center gap-3 text-sm font-light text-ink/85">
                  <Check size={16} /> {status.msg}
                </div>
              )}
              {status.state === "error" && (
                <div data-testid="rrhh-error" className="flex items-center gap-3 text-sm font-light text-red-700">
                  <AlertCircle size={16} /> {status.msg}
                </div>
              )}
            </form>
          </div>
        </div>
      </section>
    </main>
  );
};

const Field = ({ name, label, type = "text", multiline = false, value, onChange, required = true, testidOverride }) => {
  const id = testidOverride || `form-field-${name}`;
  return (
    <label className="block group">
      <span className="overline block mb-3 text-ink/60 group-focus-within:text-ink transition-colors">{label}</span>
      {multiline ? (
        <textarea
          name={name}
          data-testid={id}
          required={required}
          rows={4}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent border-b border-ink/30 focus:border-ink outline-none py-2 font-light text-lg resize-none"
        />
      ) : (
        <input
          type={type}
          name={name}
          data-testid={id}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent border-b border-ink/30 focus:border-ink outline-none py-2 font-light text-lg"
        />
      )}
    </label>
  );
};

export default TrabajaConNosotros;
